import React, { useState } from "react";
import { Avatar, Collapse, IconButton, Typography } from "@mui/material";
import MUILink from "@mui/material/Link";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import { patientSearchResult } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/models/patient";
import classes from "./global-search.module.css";
import {
  describeEstimate,
  describeIoc,
  describeVob,
  estimatePath,
  finAdvisorPatientPath,
  getFullName,
  getInitials,
  getPatientEstimates,
  getPatientId,
  getPatientIocs,
  getPatientMeta,
  getPatientVobs,
  hubPath,
  latestId,
  patientRecordPath,
  useGlobalSearchAccess,
  vobPath,
} from "./global-search-utils";
import { useClientNamesById } from "./global-search-client-filter";

interface GlobalSearchPatientDetailProps {
  patient: patientSearchResult;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

interface SubItem {
  key: string;
  title: string;
  subtitle: string;
  onClick?: () => void;
}

interface SectionRowProps {
  icon: React.ReactNode;
  title: string;
  count?: number;
  subtitle: string;
  disabled?: boolean;
  expanded?: boolean;
  onClick?: () => void;
  /** Renders the title as a link; clicking it does not toggle the row. */
  onTitleClick?: () => void;
  testId: string;
}

const SectionRow = (props: SectionRowProps) => {
  const { icon, title, count, subtitle, disabled, expanded, onClick, onTitleClick, testId } = props;
  const isExpandable = expanded !== undefined;
  return (
    <div
      className={`${classes.detailSection} ${disabled ? classes.detailSectionDisabled : ""}`}
      onClick={disabled ? undefined : onClick}
      role="button"
      aria-disabled={disabled}
      aria-expanded={isExpandable ? expanded : undefined}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      test-id={testId}
    >
      <span className={classes.detailSectionIcon}>{icon}</span>
      <div className={classes.detailSectionText}>
        <Typography variant="body2" className={classes.detailSectionTitle}>
          {onTitleClick && !disabled ? (
            <MUILink
              component="button"
              type="button"
              className={classes.sectionTitleLink}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onTitleClick();
              }}
              onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
              test-id={`${testId}-link`}
            >
              {title}
            </MUILink>
          ) : (
            title
          )}
          {count !== undefined && <span className={classes.countBadge}>{count}</span>}
        </Typography>
        <div className={classes.detailSectionSubtitle}>{subtitle}</div>
      </div>
      {isExpandable ? (
        expanded ? (
          <ExpandMoreIcon fontSize="small" className={classes.chevron} />
        ) : (
          <ChevronRightIcon fontSize="small" className={classes.chevron} />
        )
      ) : (
        <ChevronRightIcon fontSize="small" className={classes.chevron} />
      )}
    </div>
  );
};

const SubItemList = ({ items }: { items: SubItem[] }) => (
  <>
    {items.map((item) => (
      <div
        key={item.key}
        className={`${classes.subItem} ${item.onClick ? "" : classes.subItemStatic}`}
        onClick={item.onClick}
        role={item.onClick ? "button" : undefined}
        tabIndex={item.onClick ? 0 : -1}
        onKeyDown={(e) => {
          if (item.onClick && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            item.onClick();
          }
        }}
      >
        <div className={classes.detailSectionText}>
          {item.onClick ? (
            <MUILink component="span" style={{ fontWeight: 500, cursor: "pointer" }}>
              {item.title}
            </MUILink>
          ) : (
            <Typography variant="body2" className={classes.detailSectionTitle}>
              {item.title}
            </Typography>
          )}
          <div className={classes.detailSectionSubtitle}>{item.subtitle}</div>
        </div>
        {item.onClick && <ChevronRightIcon fontSize="small" className={classes.chevron} />}
      </div>
    ))}
  </>
);

type ExpandableSection = "vobs" | "estimates" | "iocs";

const GlobalSearchPatientDetail = (props: GlobalSearchPatientDetailProps) => {
  const { patient, onClose, onNavigate } = props;
  const source = patient._source;
  const patientId = getPatientId(source);
  const clientNamesById = useClientNamesById();
  const access = useGlobalSearchAccess();
  const meta = getPatientMeta(source, clientNamesById);
  const contactLine = [source.phoneNumber, source.email].filter(Boolean).join(" · ");

  const [expanded, setExpanded] = useState<ExpandableSection | null>(null);
  const toggle = (section: ExpandableSection) =>
    setExpanded((prev) => (prev === section ? null : section));

  const vobs = getPatientVobs(source);
  const estimates = getPatientEstimates(source);
  const iocs = getPatientIocs(source);

  const goToPatient = () => {
    if (patientId) onNavigate(finAdvisorPatientPath(patientId));
  };

  const latestVobId = latestId(vobs, (vob) => vob.vobId);
  const latestEstimateId = latestId(estimates, (est) => est.pfrEstimateId);
  const latestIocId = latestId(iocs, (ioc) => ioc.patientEncounterId);

  const goTo = (buildPath: (pid: number) => string) =>
    patientId ? () => onNavigate(buildPath(patientId)) : undefined;

  const newestFirst = <T,>(items: T[], getId: (item: T) => number | undefined) =>
    [...items].sort((a, b) => (getId(b) ?? 0) - (getId(a) ?? 0));

  const vobItems: SubItem[] = newestFirst(vobs, (vob) => vob.vobId).map((vob, idx) => ({
    key: `vob-${vob.vobId ?? idx}`,
    title: vob.vobId ? `#${vob.vobId}` : "VOB",
    subtitle: describeVob(vob),
    onClick: vob.vobId ? goTo((pid) => vobPath(pid, vob.vobId as number)) : undefined,
  }));

  const estimateItems: SubItem[] = newestFirst(estimates, (est) => est.pfrEstimateId).map(
    (est, idx) => ({
      key: `est-${est.pfrEstimateId ?? idx}`,
      title: est.pfrEstimateId ? `#${est.pfrEstimateId}` : "Estimate",
      subtitle: describeEstimate(est),
      onClick: est.pfrEstimateId
        ? goTo((pid) => estimatePath(pid, est.pfrEstimateId as number))
        : undefined,
    })
  );

  const iocItems: SubItem[] = newestFirst(iocs, (ioc) => ioc.patientEncounterId).map((ioc) => ({
    key: `ioc-${ioc.patientEncounterId}`,
    title: `#${ioc.patientEncounterId}`,
    subtitle: describeIoc(ioc),
    onClick: goTo((pid) => patientRecordPath(pid, ioc.patientEncounterId)),
  }));

  const firstOrEmpty = (items: SubItem[], emptyLabel: string) =>
    items.length > 0
      ? [items[0].title, items[0].subtitle].filter(Boolean).join(" · ")
      : emptyLabel;

  return (
    <div test-id="global-search-patient-detail">
      <div className={classes.detailHeader}>
        <Avatar className={classes.avatar}>{getInitials(source)}</Avatar>
        <div className={classes.patientText}>
          {access.finAdvisor ? (
            <MUILink
              component="span"
              style={{ fontWeight: 600, cursor: patientId ? "pointer" : "default" }}
              onClick={goToPatient}
            >
              {getFullName(source)}
            </MUILink>
          ) : (
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {getFullName(source)}
            </Typography>
          )}
          <div className={classes.patientMeta} title={meta}>
            {meta}
          </div>
          {contactLine && (
            <div className={classes.patientMeta} title={contactLine}>
              {contactLine}
            </div>
          )}
        </div>
        <IconButton size="small" aria-label="Close patient details" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {access.finAdvisor && (
        <>
          <SectionRow
            icon={<PersonOutlineIcon fontSize="small" />}
            title="Patient Profile"
            subtitle="Demographics, insurance, contacts"
            onClick={goToPatient}
            disabled={!patientId}
            testId="global-search-section-profile"
          />

          <SectionRow
            icon={<VerifiedUserOutlinedIcon fontSize="small" />}
            title="VOBs"
            count={vobs.length}
            subtitle={firstOrEmpty(vobItems, "No VOBs")}
            expanded={expanded === "vobs"}
            onClick={() => toggle("vobs")}
            onTitleClick={latestVobId ? goTo((pid) => vobPath(pid, latestVobId)) : undefined}
            disabled={vobs.length === 0}
            testId="global-search-section-vobs"
          />
          <Collapse in={expanded === "vobs"} timeout="auto" unmountOnExit>
            <SubItemList items={vobItems} />
          </Collapse>

          <SectionRow
            icon={<RequestQuoteOutlinedIcon fontSize="small" />}
            title="Estimates"
            count={estimates.length}
            subtitle={firstOrEmpty(estimateItems, "No estimates")}
            expanded={expanded === "estimates"}
            onClick={() => toggle("estimates")}
            onTitleClick={
              latestEstimateId ? goTo((pid) => estimatePath(pid, latestEstimateId)) : undefined
            }
            disabled={estimates.length === 0}
            testId="global-search-section-estimates"
          />
          <Collapse in={expanded === "estimates"} timeout="auto" unmountOnExit>
            <SubItemList items={estimateItems} />
          </Collapse>
        </>
      )}

      {access.paymentHub && (
        <SectionRow
          icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
          title="Payment Hub"
          subtitle="Payment programs and payments"
          onClick={goTo(hubPath)}
          disabled={!patientId}
          testId="global-search-section-payment-hub"
        />
      )}

      {access.finAdvisor && (
        <>
          <SectionRow
            icon={<FolderOpenOutlinedIcon fontSize="small" />}
            title="IOCs"
            count={iocs.length}
            subtitle={firstOrEmpty(iocItems, "No IOCs")}
            expanded={expanded === "iocs"}
            onClick={() => toggle("iocs")}
            onTitleClick={
              latestIocId ? goTo((pid) => patientRecordPath(pid, latestIocId)) : undefined
            }
            disabled={iocs.length === 0}
            testId="global-search-section-iocs"
          />
          <Collapse in={expanded === "iocs"} timeout="auto" unmountOnExit>
            <SubItemList items={iocItems} />
          </Collapse>
        </>
      )}
    </div>
  );
};

export default GlobalSearchPatientDetail;
